import { FileObject } from "@citychallenge/common";

export interface MdFileMap {
  [key: string]: FileObject;
}

const TokensBlock = {
  empty: /^\s*$/, // Skip empty
  // Group 1: caption, group 2: url, group 3: text (for when text follows the image)
  image: /^!\[(.*)\]\((.+)\) *(.*)$/,
  heading: /^# (.+)$/, // Group 1: Heading
  unorderedList: /^\* (.+)$/, // Group 1: unorderList
  orderedList: /^\d\. (.+)$/, // Group 1: orderedList
  seperator: /^---$/, // No groups
  youtube: /^\[([^\]]*)\]\(([^\)]+youtube\.com[^\)]+)\)$/, // Group 1: caption, group 2: url
  blockQuote: /^> (.+)$/, // Group 1 content
  paragraph: /^([^\n]+)$/, // All other values
};

const TokensInline = {
  link: /\[([^\[\]]+)\]\(([^\(\)]*)\)/, // Group 1: caption, group 2: url
  linkSimple: /<([^>]+)>/, // Group 1: caption, group 2: url
  strike: /~~((?:(?!~~).)*)~~/, // Group 1 : text
  boldItalic: /\*\*\*((?:(?!\*\*\*).)*)\*\*\*/, // Group 1: text
  bold: /\*\*((?:(?!\*\*).)*)\*\*/, // Group 1: text
  italicUnderscore: /_([^_]*)_/, // Group 1: text,
  italic: /\*([^\*]+)\*/, // Group 1: text
};

export type TokensBlockKey = keyof typeof TokensBlock;
export type TokensInlineKey = keyof typeof TokensInline;

export interface BlockLineResult {
  token: TokensBlockKey;
  values: string[];
}

export interface BlockLineResultSequential extends BlockLineResult {
  key: string;
  sequence: number;
  prevToken?: TokensBlockKey;

  totaLines: number;
  currentLine: number;
}

export class MarkdownBaseParser {
  public static TokensBlock = TokensBlock;

  public static TokensInline = TokensInline;

  public static TokensBlockList = Object.entries(
    MarkdownBaseParser.TokensBlock
  );

  public static TokensInlineList = Object.entries(
    MarkdownBaseParser.TokensInline
  );

  public static parseLine(line: string): BlockLineResult {
    for (const [token, regex] of MarkdownBaseParser.TokensBlockList) {
      const results = regex.exec(line);

      if (results) {
        return {
          token: token as keyof typeof TokensBlock,
          values: results.splice(1, results.length - 1),
        };
      }
    }
    return { token: "empty", values: [] };
  }

  public static unescape = (text: string) => {
    return text
      .replace(/\\([\\`*{}[\]()#+\-.!_>])/g, "$1")
      .replace(/(\u2028)/g, "");
  };

  public static parsePreview(content = "") {
    if (!content) return "";
    const lines = content.split("\n");
    const results: BlockLineResult[] = [];

    for (const line of lines) {
      results.push(MarkdownBaseParser.parseLine(line));
    }

    const firstParagraph = results.find((t) => t.token === "paragraph");

    if (firstParagraph) {
      const text = firstParagraph.values[0] || "";
      const replacedText = text.replace(/[\[\]\(\*~_]/g, "");
      return replacedText;
    }

    return undefined;
  }

  public static parseFlat(content = "") {
    if (!content) return "";
    const lines = content.split("\n");
    const results: BlockLineResult[] = [];

    for (const line of lines) {
      results.push(MarkdownBaseParser.parseLine(line));
    }

    return results
      .map((t) => {
        return t.values.map((t) => t.replace(/[\[\]\(\*~_]/g, "")).join(" ");
      })
      .join(" ");
  }
}
